/**
 * G2eGente - Minhas Avaliações (App Mobile Funcionário)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeSelfAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft,
  ClipboardCheck,
  Loader2,
  Star,
  ChevronRight,
  Calendar,
  CheckCircle2,
  Clock,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';

const CLASSIFICACAO_COLORS = {
  excepcional: 'bg-emerald-100 text-emerald-700',
  supera: 'bg-green-100 text-green-700',
  atende: 'bg-yellow-100 text-yellow-700',
  abaixo: 'bg-red-100 text-red-700'
};

export default function EmployeeEvaluations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('received');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await employeeSelfAPI.getMyEvaluations();
      setData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const receivedReviews = data?.reviews?.filter(r => r.tipo !== 'autoavaliacao') || [];
  const selfReviews = data?.reviews?.filter(r => r.tipo === 'autoavaliacao') || [];
  const pendingSelf = data?.pending_self_evaluations || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employee-evaluations">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Minhas Avaliações</h1>
            <p className="text-sm text-slate-500">Acompanhe seu desempenho</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="received">
              Recebidas ({receivedReviews.length})
            </TabsTrigger>
            <TabsTrigger value="self">
              Autoavaliações ({selfReviews.length})
            </TabsTrigger>
          </TabsList>

          {/* Received Evaluations */}
          <TabsContent value="received" className="space-y-3 mt-4">
            {receivedReviews.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-900">Nenhuma avaliação recebida</h3>
                  <p className="text-sm text-slate-500 mt-2">
                    Suas avaliações de desempenho aparecerão aqui
                  </p>
                </CardContent>
              </Card>
            ) : (
              receivedReviews.map(review => (
                <Card 
                  key={review.id} 
                  className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/m/employee/evaluations/${review.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-slate-900">{review.cycle_nome}</h3>
                          {review.status === 'finalizado' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{review.periodo}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {review.tipo === 'gestor' ? 'Avaliação do Gestor' : 'Avaliação de Pares'}
                          </Badge>
                          {review.classificacao && (
                            <Badge className={CLASSIFICACAO_COLORS[review.classificacao]}>
                              {review.classificacao}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {review.media_geral ? (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-bold text-lg">{review.media_geral.toFixed(1)}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">Pendente</span>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400 mt-2 ml-auto" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Self Evaluations */}
          <TabsContent value="self" className="space-y-3 mt-4">
            {/* Pending Self-Evaluations */}
            {pendingSelf.length > 0 && (
              <div className="space-y-3 mb-4">
                <h3 className="font-medium text-amber-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pendentes
                </h3>
                {pendingSelf.map(item => (
                  <Card 
                    key={item.cycle_id} 
                    className="border-amber-200 bg-amber-50 cursor-pointer"
                    onClick={() => navigate(`/m/employee/self-evaluation?cycle=${item.cycle_id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-amber-900">{item.cycle_nome}</h4>
                          <p className="text-sm text-amber-700">{item.periodo}</p>
                          <p className="text-xs text-amber-600 mt-1">Prazo: {item.prazo}</p>
                        </div>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                          <Edit className="w-4 h-4 mr-1" />
                          Fazer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Completed Self-Evaluations */}
            {selfReviews.length === 0 && pendingSelf.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Edit className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-900">Nenhuma autoavaliação</h3>
                  <p className="text-sm text-slate-500 mt-2">
                    Quando houver ciclos abertos, você poderá fazer sua autoavaliação
                  </p>
                </CardContent>
              </Card>
            ) : (
              selfReviews.map(review => (
                <Card 
                  key={review.id} 
                  className="border-slate-200"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-slate-900">{review.cycle_nome}</h3>
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-sm text-slate-500">{review.periodo}</p>
                        <Badge variant="outline" className="text-xs mt-2">
                          Autoavaliação
                        </Badge>
                      </div>
                      <div className="text-right">
                        {review.media_geral && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-bold text-lg">{review.media_geral.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
